import { ScheduledReportResponse, ScheduledReportReport } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { InlineListSectionHandler, InlineListItem } from '../shared/inline-list.section';
import { ReportParameterModalComponent, ReportSelectModalComponent } from 'app/shared/components/reports';
import { ReportingService } from 'app/shared/components/reports';
import * as uuid from 'uuid';
import { ERRORS } from 'app/shared/model/errors.model';
import { NotificationService } from 'app/services';

export interface ListSectionValues {
    items: any;
}

export interface ReportListItem extends InlineListItem {
    data: ScheduledReportReport;
}


export class ReportListSectionHandler extends InlineListSectionHandler<ScheduledReportResponse> {

    emptyText = this.translate('MESSAGES.EMPTY_LIST');
    maxReports = 10;
    maxAssets = 6;

    constructor(
        admin: AdminEditorService,
        ownerId: string,
        private reportingService: ReportingService,
        private notify: NotificationService
    ) {
        super(admin, ownerId);
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SCHEDULEDREPORT.REPORTS.${key}`, values);
    }

    async getItems(entity: ScheduledReportResponse, _offset: number, _limit: number): Promise<ReportListItem[]> {
        const definitions = await this.reportingService.loadReportDefinitions();
        return Object.keys(entity.reports).map(key => {
            const item = entity.reports[key];
            const defId = item.config.definitionId + '-' + (item.config.parameters.style || 'default');
            const def = definitions.find(x => x.id === defId);
            return {
                id: key,
                name: item.title,
                description: def ? def.description : '',
                icon: 'report',
                data: item,
            };
        });
    }

    async actionClicked(action: string, entity: ScheduledReportResponse, item: ReportListItem): Promise<ScheduledReportResponse> {
        try {
            switch (action) {
                case 'add': return this.showAddModal(entity);
                case 'delete': return this.showDeleteConfirmation(entity, item);
                case 'click': return this.showEditModal(entity, item);
                default:
                    return entity;
            }
        } catch (err) {
            this.admin.modal.error(this.translate('MESSAGES.UNEXPECTED_ERROR'), err);
        }
    }

    async showAddModal(entity: ScheduledReportResponse): Promise<ScheduledReportResponse> {
        if (entity && entity.reports && Object.keys(entity.reports).length >= this.maxReports) {
            this.admin.modal.alert(this.translate('MESSAGES.LIMIT_REACHED.DESCRIPTION', { max: this.maxReports }), this.translate('MESSAGES.LIMIT_REACHED.TITLE'));
            return entity;
        }
        const report = await ReportSelectModalComponent.open(this.admin.modal, { clientId: entity.owner.id, schedulableOnly: true });
        if (report) {

            return await this.showEditModal(entity, {
                id: uuid.v4(),
                description: report.description,
                name: report.name,
                icon: 'report',
                data: {
                    title: report.name,
                    outputFormat: 'pdf',
                    config: {
                        definitionId: report.id.substr(0, 36),
                        parameters: {},
                        styleId: report.id.substr(37),
                    },
                },
            });
        }
        return entity;
    }

    async showDeleteConfirmation(entity: ScheduledReportResponse, item: ReportListItem): Promise<ScheduledReportResponse> {
        const result = await this.admin.modal.confirm(this.translate('MESSAGES.CONFIRM_DELETE'));
        if (result) {

            return this.admin.applyUpdate(api => api.entities.updateScheduledReport(entity.id, {
                reports: {
                    [item.id]: null,
                },
            }), { _type: 'scheduledreport' });

        }
        return entity;
    }

    async showEditModal(entity: ScheduledReportResponse, item: ReportListItem): Promise<ScheduledReportResponse> {

        const defId = item.data.config.definitionId + '-' + (item.data.config.styleId || 'default');
        const def = await this.reportingService.getReportDefinition(defId);
        if (def) {
            const report = await ReportParameterModalComponent.open(this.admin.modal, {
                clientId: this.ownerId,
                title: item.data.title,
                reportDefinition: def,
                parameters: {
                    id: item.data.config.definitionId,
                    layout: item.data.config.parameters.layout,
                    params: item.data.config.parameters,
                },
                isSheduledReport: true,
            });

            if (report) {

                item.data = {
                    title: report.title,
                    outputFormat: report.outputFormat,
                    config: report.config,
                };
                
                try {
                    return await this.admin.applyUpdate(api => api.entities.updateScheduledReport(entity.id, {
                        reports: {
                            [item.id]: item.data,
                        },
                    }), { _type: 'scheduledreport' });
                } catch (err) {
                    const errorMessage = this.getErrorMessage(err);
                    let message;
                    if (errorMessage.includes('assetSelection too large')) {
                        message = {
                            title: 'ASSET_LIMIT_REACHED.TITLE',
                            desc: this.translate('MESSAGES.ASSET_LIMIT_REACHED.DESC', { max: this.maxAssets }),
                        };
                    } else {
                        message = {
                            title: 'UNEXPECTED_ERROR',
                            desc: errorMessage,
                        };
                    }
                    this.admin.modal.error(this.translate(`MESSAGES.${message.title}`), new Error(message.desc));
                }
                
            }

        } else {
            this.admin.modal.error(this.translate('MESSAGES.REPORT_NOT_FOUND'));
        }

    }

    getErrorMessage(error: any) {
        if (error && error.name === ERRORS.VALIDATE_ERROR && error.data && error.data.fields) {
            const errors = Object.keys(error.data.fields).reduce((result, field) => {
                result.push(error.data.fields[field].message);
                return result;
            }, []);
            return errors.join(', ');
        } else {
            return this.notify.translateError(error);
        }
    }
}
